import { BidOrchestratorPhase, BidOrchestratorSection } from "../../types/graphQLEnums";
import { BidOrchestratorEntry, SectionStatus, SectionStatusMap } from "./types";

/**
 * Maps a lifecycle event onto the per-section status map. Started events flip the
 * matching section(s) to `streaming`, success events to `complete`, and run-wide
 * terminal events resolve any still-pending sections. Idempotent on a no-op transition.
 */
export function updateSectionStreamingStatus(
  newStatus: BidOrchestratorPhase,
  nextEntry: BidOrchestratorEntry,
  previousStatus: BidOrchestratorPhase,
): BidOrchestratorEntry {
  switch (newStatus) {
    // Win themes and qualification summary run downstream of the qualification report
    // workflow, so this event is the earliest point we know both will eventually stream.
    case BidOrchestratorPhase.QualificationReportStarted:
      nextEntry = updateSectionStatus(
        nextEntry,
        BidOrchestratorSection.QualificationReport,
        "streaming",
      );
      nextEntry = updateSectionStatus(
        nextEntry,
        BidOrchestratorSection.QualificationSummary,
        "streaming",
      );
      nextEntry = updateSectionStatus(nextEntry, BidOrchestratorSection.WinThemes, "streaming");
      return nextEntry;
    case BidOrchestratorPhase.RequirementSummaryReportStarted:
      return updateSectionStatus(
        nextEntry,
        BidOrchestratorSection.RequirementsSummary,
        "streaming",
      );
    case BidOrchestratorPhase.QualificationReportGenerationSuccess:
      return updateSectionStatus(nextEntry, BidOrchestratorSection.QualificationReport, "complete");
    case BidOrchestratorPhase.RequirementSummaryReportGenerationSuccess:
      return updateSectionStatus(nextEntry, BidOrchestratorSection.RequirementsSummary, "complete");

    // Run-wide terminal events lock in any section not already resolved.
    case BidOrchestratorPhase.Failed: {
      if (previousStatus !== BidOrchestratorPhase.Failed) {
        nextEntry.error = "bid orchestrator failed";
      }
      return resolvePendingSections(nextEntry, "failed");
    }
    case BidOrchestratorPhase.Success:
      return resolvePendingSections(nextEntry, "complete");
    default:
      return nextEntry;
  }
}

/**
 * Sets one section's status on the entry. Returns the entry unchanged on a no-op
 * transition so referential identity is preserved for memoised consumers.
 */
export function updateSectionStatus(
  entry: BidOrchestratorEntry,
  section: keyof SectionStatusMap,
  status: SectionStatus,
): BidOrchestratorEntry {
  if (entry.sectionStatus[section] === status) return entry;
  return {
    ...entry,
    sectionStatus: { ...entry.sectionStatus, [section]: status },
  };
}

/**
 * Locks every still-pending section into a terminal status when the run-wide
 * success/failure event arrives. Sections already complete/failed are left alone.
 */
function resolvePendingSections(
  entry: BidOrchestratorEntry,
  resolution: "complete" | "failed",
): BidOrchestratorEntry {
  const next: SectionStatusMap = { ...entry.sectionStatus };
  let changed = false;
  for (const section of Object.keys(next) as Array<keyof SectionStatusMap>) {
    if (next[section] === "complete" || next[section] === "failed") continue;
    next[section] = resolution;
    changed = true;
  }
  return changed ? { ...entry, sectionStatus: next } : entry;
}

const BEFORE_REPORT_STREAMING_PHASES = [
  BidOrchestratorPhase.ProcessingFiles,
  BidOrchestratorPhase.StartingBidDetailsExtraction,
  BidOrchestratorPhase.BidDetailsExtractionSuccess,
  BidOrchestratorPhase.FilesSuccessfullyProcessed,
  BidOrchestratorPhase.StartingBidReportGeneration,
];

/**
 * For each report section, derive whether it's currently streaming based on the
 * section-specific status values and the overall orchestrator phase. Also derive aggregate
 * "isStreaming" to indicate if ANY section is still actively streaming. This is used to drive
 * loading states in the UI, and also to determine whether to fold streamed content into the
 * React Query cache or wait for the persisted report to hydrate (see QualificationReport.tsx).
 * @param entry
 * @returns
 */
export function getisSectionStreamingVals(entry: BidOrchestratorEntry) {
  const isQualificationReportStreaming =
    entry.sectionStatus[BidOrchestratorSection.QualificationReport] === "streaming";
  const isRequirementSummaryStreaming =
    entry.sectionStatus[BidOrchestratorSection.RequirementsSummary] === "streaming";
  const isQualificationSummaryStreaming =
    entry.sectionStatus[BidOrchestratorSection.QualificationSummary] === "streaming";
  const isWinThemeStreaming = entry.sectionStatus[BidOrchestratorSection.WinThemes] === "streaming";
  // Pre-section phases (file processing, extraction, generation kickoff) count as
  // streaming for the aggregate flag so the UI keeps a spinner before any
  // section-specific event has landed.
  const inPreSectionStreamingPhase = BEFORE_REPORT_STREAMING_PHASES.includes(entry.phase);

  const isStreaming =
    isQualificationReportStreaming ||
    isRequirementSummaryStreaming ||
    isQualificationSummaryStreaming ||
    isWinThemeStreaming ||
    inPreSectionStreamingPhase;

  return {
    isQualificationReportStreaming,
    isRequirementSummaryStreaming,
    isQualificationSummaryStreaming,
    isWinThemeStreaming,
    isStreaming,
  };
}
